import fetcher from "@/core/lib/fetcher";
import { APIResponse } from "@/core/types/utils";
import {
	queryOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import {
	CancellationFormValues,
	ReservationDetails,
	Reservations,
	ReservationsQuery,
} from "./reservations.type";

type ReservationsFilters = Pick<
	ReservationsQuery,
	| "source"
	| "dateMode"
	| "fromDate"
	| "endDate"
	| "checkInFrom"
	| "checkInTo"
	| "status"
	| "purchaseId"
	| "guestName"
	| "promoCode"
	| "agentId"
>;

// Builds the URLSearchParams shared by the paginated list and the CSV export
// endpoints. Centralizing this means any future filter (e.g. country) is added
// once and both callers pick it up automatically.
const buildReservationsFilterParams = (
	q: ReservationsFilters,
): URLSearchParams => {
	const sp = new URLSearchParams();
	sp.set("origin", q.source);
	if (q.dateMode === "booking" && q.fromDate && q.endDate) {
		sp.set("fromDate", q.fromDate);
		sp.set("endDate", q.endDate);
	}
	if (q.dateMode === "checkIn" && q.checkInFrom && q.checkInTo) {
		sp.set("checkInFrom", q.checkInFrom);
		sp.set("checkInTo", q.checkInTo);
	}
	if (q.status) sp.set("status", q.status);
	if (q.purchaseId) sp.set("purchaseId", q.purchaseId);
	if (q.guestName) sp.set("guestName", q.guestName);
	if (q.promoCode) sp.set("promoCode", q.promoCode);
	if (q.agentId) sp.set("agentId", q.agentId);
	return sp;
};

export const reservationsQueryOptions = (q: ReservationsQuery) =>
	queryOptions({
		queryKey: ["reservations", q],
		queryFn: () => {
			const sp = buildReservationsFilterParams(q);
			sp.set("page", String(q.page));
			sp.set("limit", String(q.limit));

			return fetcher
				.auth(`Bearer ${q.token}`)
				.url(
					`${import.meta.env.VITE_API_BOOKING}property/${q.siteId}/bookings?${sp.toString()}`,
				)
				.get()
				.json<APIResponse<Reservations>>();
		},
	});

/**
 * Downloads reservations matching the current filters as a CSV blob.
 * The backend caps the result set; when the cap is hit, the response carries
 * `X-Export-Truncated: true` and `truncated` is surfaced to the caller so the
 * UI can prompt the user to refine filters.
 */
export const downloadReservationsCsv = (
	q: Omit<ReservationsQuery, "page" | "limit">,
): Promise<{ blob: Blob; truncated: boolean }> => {
	const sp = buildReservationsFilterParams(q);
	return fetcher
		.auth(`Bearer ${q.token}`)
		.url(
			`${import.meta.env.VITE_API_BOOKING}property/${q.siteId}/bookings/csv?${sp.toString()}`,
		)
		.get()
		.res(async (res) => ({
			blob: await res.blob(),
			truncated: res.headers.get("X-Export-Truncated") === "true",
		}));
};

export const reservationByIdQueryOptions = (
	siteId: string,
	purchaseId: string,
	token: string,
) =>
	queryOptions({
		queryKey: ["reservation", siteId, purchaseId, token],
		queryFn: () =>
			fetcher
				.auth(`Bearer ${token}`)
				.url(
					import.meta.env.VITE_API_BOOKING +
						`property/${siteId}/bookings/purchase/${purchaseId}`,
				)
				.get()
				.json<APIResponse<ReservationDetails>>(),
	});

export const useCancelReservation = (
	token: string,
	id: string,
	siteId: string,
	purchaseId: string,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CancellationFormValues) =>
			fetcher
				.auth(`Bearer ${token}`)
				.url(
					import.meta.env.VITE_API_BOOKING + `property/bookings/cancel/${id}`,
				)
				.post(data)
				.json(),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: ["reservation", siteId, purchaseId],
			});
		},
	});
};
export const useCancelTotalReservation = (
	token: string,
	id: string,
	siteId?: string,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CancellationFormValues) =>
			fetcher
				.auth(`Bearer ${token}`)
				.url(
					import.meta.env.VITE_API_BOOKING +
						`property/bookings/purchase/${id}?propertyId=${siteId}`,
				)
				.post(data)
				.json(),
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: ["reservations"],
			});
		},
	});
};
